import { useTranslation } from 'react-i18next';
import type { Prayer } from '@/types/prayer';
import type { SelectedVerse } from '@/components/ShareVersePicker';
import {
  formatPrayedOnForCard,
  normalizeCardLanguage,
} from '@/lib/card-date';
import styles from './MailCardPreview.module.css';

interface MailCardPreviewProps {
  prayer: Prayer;
  includeVerse: boolean;
  selectedVerse: SelectedVerse | null;
  includeCardLabel: boolean;
  /** Stripped cardLabel noun, or null when absent (es/pt, abstract,
   *  or no-match English). Null renders the no-"about" body. */
  cardLabelNoun: string | null;
  /** Recipient name shown on the card front. Null/empty when Q3 hasn't committed yet. */
  recipientName?: string | null;
  /** Sender name shown as the inside-page-1 signature.
   *  Null/empty when Q4 hasn't committed yet. */
  senderName?: string | null;
}

/**
 * Multi-page card preview frame (front + inside-page-1
 * + inside-page-2 message slots) mirroring the Cardly template
 * + messages.pages + Style contract per plan § 4.4 + strategic
 * plan § 8.2 round-13 amendment. Specific Cardly template ID is
 * M.4-deferred; this component is "Cardly-shape-aligned but
 * template-unspecified" today.
 *
 * Preview SCOPE: models the body composition that fills the
 * Cardly template's text-area slots (messages.pages), NOT the
 * full Cardly artwork model. Cardly templates carry a four-page
 * artwork structure (front exterior + inside spreads); when no
 * verse is selected the preview omits the inside-page-2 *message*
 * slot — the M.4 template-picking chunk reconciles preview slots
 * <-> Cardly artwork pages.
 *
 * Cardly Style defaults (placeholder until M.4 template picks
 * concrete values): align center, verticalAlign middle, font
 * Georgia serif, size scales with text preference (rem body), color
 * --color-text on --color-bg.
 *
 * Date string formatted via formatPrayedOnForCard from
 * src/lib/card-date.ts which mirrors the server's
 * formatPrayedOnLocal output shape. Locale normalization via
 * normalizeCardLanguage (closes round-2 BLOCKER: prevents an
 * unsupported i18n.language tag like 'fr-FR' from silently
 * formatting in the browser default locale).
 *
 * Phase 2B i18n keys consumed: mailCard.previewFrontLabel,
 * mailCard.previewInsidePage1Label, mailCard.previewInsidePage2Label,
 * mailCard.previewBody, mailCard.previewBodyWithCardLabel,
 * mailCard.previewVerseAriaLabel, mailCard.previewDevDisclaimer.
 */
export default function MailCardPreview({
  prayer,
  includeVerse,
  selectedVerse,
  includeCardLabel,
  cardLabelNoun,
  recipientName,
  senderName,
}: MailCardPreviewProps) {
  const { t, i18n } = useTranslation();

  const language = normalizeCardLanguage(i18n.language);
  const tz =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const dayString = formatPrayedOnForCard(
    prayer.createdAt,
    tz,
    language,
  );

  const body =
    includeCardLabel && cardLabelNoun
      ? t('mailCard.previewBodyWithCardLabel', {
          day: dayString,
          noun: cardLabelNoun,
        })
      : t('mailCard.previewBody', { day: dayString });

  const showInsidePage2 = includeVerse && selectedVerse !== null;
  const showDevDisclaimer =
    import.meta.env.VITE_SHOW_DEV_DISCLAIMER === 'true';

  const trimmedRecipient = recipientName?.trim();
  const trimmedSender = senderName?.trim();

  return (
    <div className={styles.preview}>
      <section
        className={styles.page}
        aria-labelledby="mail-card-preview-front-label"
      >
        <div
          className={styles.pageLabel}
          id="mail-card-preview-front-label"
        >
          {t('mailCard.previewFrontLabel')}
        </div>
        <div className={`${styles.pageContent} ${styles.frontContent}`}>
          {trimmedRecipient ? (
            <div className={styles.frontRecipient}>{trimmedRecipient}</div>
          ) : (
            <div className={styles.frontGlyph} aria-hidden="true">
              &#10047;
            </div>
          )}
        </div>
      </section>

      <section
        className={styles.page}
        aria-labelledby="mail-card-preview-inside-1-label"
      >
        <div
          className={styles.pageLabel}
          id="mail-card-preview-inside-1-label"
        >
          {t('mailCard.previewInsidePage1Label')}
        </div>
        <div className={styles.pageContent}>
          <p className={styles.bodyText}>{body}</p>
          {trimmedSender && (
            <p className={styles.signature}>— {trimmedSender}</p>
          )}
        </div>
      </section>

      {showInsidePage2 && (
        <section
          className={styles.page}
          aria-labelledby="mail-card-preview-inside-2-label"
        >
          <div
            className={styles.pageLabel}
            id="mail-card-preview-inside-2-label"
          >
            {t('mailCard.previewInsidePage2Label')}
          </div>
          <div className={styles.pageContent}>
            <div
              className={styles.verseWrap}
              aria-label={t('mailCard.previewVerseAriaLabel', {
                reference: selectedVerse!.reference,
              })}
            >
              <p className={styles.verseText}>{selectedVerse!.text}</p>
              <p className={styles.verseReference}>
                {selectedVerse!.reference}
              </p>
            </div>
          </div>
        </section>
      )}

      {showDevDisclaimer && (
        <div className={styles.devDisclaimer}>
          {t('mailCard.previewDevDisclaimer')}
        </div>
      )}
    </div>
  );
}
